// v2/SingleCard.js  (plain JS)
import React, { forwardRef } from 'react';
import moment from 'moment-timezone';           // tz build
import styles from './SingleCard.module.scss';
import nowPlayingSvg from '../assets/nowPlaying.svg';
import hasPregame    from '../assets/hasPregame.svg';
import { EventStatusDictionary } from '../helpers/eventHelper';


/* ───────────────────────────────────────────── component ── */
const SingleCardInner = forwardRef(
  ({ event, isNowPlaying, now, tz, onToggleSubscription, tabIndex }, ref) => {

    /* ----------------------------- helper flags */
    const isLiveByClock =
      event.schedule_start &&
      event.schedule_end &&
      moment.tz(now, tz).isBetween(
        moment.tz(event.schedule_start, tz),
        moment.tz(event.schedule_end, tz),
        null,
        '[)'
      );

    const isLive          = isNowPlaying || isLiveByClock;
    const isScheduledFlag =
      !isLive && (event.status === EventStatusDictionary.SCHEDULED || event.status === EventStatusDictionary.COMING);

    const weekdayLabel = (() => {
      if (!isScheduledFlag) return null;

      const start = moment.tz(event.schedule_start, tz);
      const today = moment.tz(now, tz);

      if (start.isSame(today, 'day'))              return 'TODAY';
      if (start.isoWeek() === today.isoWeek())     return start.format('dddd').toUpperCase();
      return start.format('ddd MM/DD').toUpperCase();
    })();

    const CHAR_THRESHOLD = 32;
    const needsMarquee   = event.title.length >= CHAR_THRESHOLD;

    const liveButtonText = isLive ? 'LIVE' : <span>{weekdayLabel ?? event.status}</span>;
    return (
      <div className={styles.eventSingle}>
        <div className={styles.buttons}>
          <button
            ref={ref}
            tabIndex={tabIndex ?? 0}
            className={styles.toggleSubscriptionButton}
            onClick={() => onToggleSubscription(event)}
          >
            <img src={event.thumbnail} alt={event.title} className={styles.imgCard} />
          </button>

          <div className={styles.liveContainer}>
            <div
              className={[
                styles.liveButton,
                event.status === 'LIVE' && styles.live,
                isNowPlaying && styles.nowPlaying,
                isScheduledFlag ? styles.scheduled : '',
              ].join(' ')}
            >
              {isNowPlaying && <img src={nowPlayingSvg} alt="Now playing" />}
              {isNowPlaying ? 'NOW PLAYING' : liveButtonText}
            </div>

            {event.schedule_start && (
              <div className={styles.hasPregame}>
                <img src={hasPregame} alt="has pregame" />
              </div>
            )}
          </div>
        </div>

        <p>
          Pregame:&nbsp;
          {moment.tz(event.schedule_start, tz).format('h:mm A')}
          {' | '}Event start:&nbsp;
          {moment
            .tz(event.information?.main_start_time ?? event.schedule_start, tz)
            .format('h:mm A')}
        </p>

        <h3
          className={`${styles.cardTitle} ${
            needsMarquee ? styles.needsMarquee : ''
          }`}
        >
          <span className={styles.marqueeTrack}>
            <span className={styles.marqueePiece}>{event.title}</span>
            {needsMarquee && (
              <>
                <span className={styles.marqueeSeparator}> | </span>
                <span className={styles.marqueePiece} aria-hidden="true">
                  {event.title}
                </span>
              </>
            )}
          </span>
        </h3>

        <p>
          { event.label }
        </p>
      </div>
    );
  }
);

/* ───────────────────────────────────── custom comparison ──
   Re-render only when:
   • the *identity* of the event changes (id)
   • “now playing” toggles
   • status jumps (e.g. SCHEDULED → LIVE)
   • thumbnail swaps (edge-case: poster rotation)
----------------------------------------------------------------*/
const areEqual = (prev, next) =>
  prev.event.id === next.event.id &&
  prev.isNowPlaying === next.isNowPlaying &&
  prev.event.status === next.event.status &&
  prev.event.thumbnail === next.event.thumbnail;

/* ─────────────────────────────────────────── export ── */
SingleCardInner.displayName = 'SingleCard';
export default React.memo(SingleCardInner, areEqual);